/**
 * /prfaq — SEO landing page targeting the "prfaq" / "pr faq" /
 * "amazon prfaq" / "working backwards document" / "press release faq"
 * cluster (~800 mo. searches, high PM intent).
 *
 * Strategy: give the searcher real value first — actually teach the
 * Amazon Working Backwards PR/FAQ method (what the press release covers,
 * what the internal vs. external FAQ each answer). Be honest that the
 * method itself is excellent; the problem is the substrate. A PR/FAQ is
 * a static document, so once it's approved and the product evolves, the
 * decisions inside it (pricing, scope, target customer) drift from
 * reality and nobody notices. Draftlize turns each of those decisions
 * into a structured card an AI agent reads and writes, where dependents
 * auto-flag stale when an upstream decision changes — and the PRD/spec
 * downstream can cite them.
 *
 * English-only + inline (no i18n), mirroring DecisionLog.tsx and the
 * comparison pages. Chinese variant lives in PrfaqZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function Prfaq() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/prfaq"
      documentTitle="PR/FAQ Template: Amazon's Working-Backwards Doc · Draftlize"
      documentDescription="What a PR/FAQ is and how to write one — Amazon's working-backwards document: a fake press release plus an internal and external FAQ, written before you build. The method is great; the catch is it's a static doc. Draftlize keeps the decisions inside a PR/FAQ — pricing, scope, target customer — from drifting as the product evolves."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Working Backwards</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            The PR/FAQ, the Amazon way —<br/>
            <em>without it going stale.</em>
          </h1>
          <p className="lede">
            The <em>PR/FAQ</em> is the centerpiece of Amazon's <em>Working Backwards</em> method: before you write a line of code, you write a fake press release announcing the finished product, plus an FAQ that interrogates it. It forces the team to start from the customer and the outcome, not the backlog. The method is genuinely good — that part isn't the problem. The problem is the artifact. A PR/FAQ is a static document, so the day it's approved is the day it starts drifting from the product you actually ship. This page teaches you how to write one properly, then shows how Draftlize keeps the <em>decisions</em> inside it — price, scope, who it's for — from quietly becoming fiction.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The method</span>
            <h2 className="serif">Working backwards, <em>in one page.</em></h2>
          </div>
          <p>A PR/FAQ has two halves: a one-page press release that imagines the launch, and an FAQ that pressure-tests it. Write it before building. If you can't make the press release compelling and the FAQ survivable, the idea isn't ready — and you just saved a quarter. Here's exactly what each part holds.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'The press release',
              hint: '1 page',
              body: 'A short, real-sounding announcement of the finished product, dated at launch. Headline, sub-headline, the problem in the customer\'s words, your solution, a quote from a leader, a quote from a delighted customer, and how to get started. Plain language, no jargon — if it doesn\'t excite a stranger, it won\'t excite the market.',
            },
            {
              k: 'The headline & problem',
              hint: 'Customer-first',
              body: 'Name the customer and the pain before the product. Working backwards means the release reads as the outcome the customer gets, not the features you built. If the problem isn\'t sharp, nothing downstream matters.',
            },
            {
              k: 'External FAQ',
              hint: 'Customer-facing',
              body: 'The questions a customer or journalist would ask: What is it, what does it cost, how is it different, when can I have it, is my data safe? These answers are the public-facing decisions — pricing, positioning, availability — stated plainly enough to publish.',
            },
            {
              k: 'Internal FAQ',
              hint: 'Team-facing',
              body: 'The hard questions leadership will ask: Why us, why now, what\'s the size of the prize, what could go wrong, what do we have to believe, what are we NOT doing? This is where scope, target customer, and the risky assumptions get pinned down — the decisions that drive every spec after.',
            },
            {
              k: 'Assumptions & risks',
              hint: 'What must be true',
              body: 'The beliefs the whole plan rests on, made explicit. "We assume customers will pay per use, not per seat." Surfacing them is the point of the exercise — and each one is a decision you\'ll either confirm or be forced to revisit.',
            },
            {
              k: 'What we are NOT doing',
              hint: 'Scope',
              body: 'The deliberate non-goals. Amazon insists on this line because scope is a decision, and an unstated non-goal becomes scope creep three sprints later. The clearest signal that a team has actually decided.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the method. Below: why the document drifts the moment it's approved — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a PR/FAQ <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It's a snapshot of a plan, frozen at approval</h3>
            <p>A PR/FAQ captures what you believed on the day it was signed off. But the product keeps moving — pricing shifts, scope narrows, the target customer sharpens. The document doesn't move with it. By launch, the press release describes a product that no longer exists, and the FAQ answers questions with last quarter's decisions.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">The decisions inside it have no downstream links</h3>
            <p>The PR/FAQ is where you decide the price, the scope, the "what we are NOT doing." Then the PRD, the specs, and the eng tickets all assume those calls. When you change the pricing decision later, nothing connects it back to the PR/FAQ or forward to the specs that depended on it — so they silently go wrong.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Nobody re-reads a doc; an agent does</h3>
            <p>The PR/FAQ does its job once — in the review meeting — and then gets filed. The assumptions it surfaced were supposed to be revisited as you learned. But a static doc isn't a reader; it can't notice that an assumption it recorded has since been disproven. So the drift accumulates unwatched.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same PR/FAQ, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each decision becomes a card, not a paragraph</h3>
            <p>Write the press release and FAQ in Draftlize and the load-bearing calls inside — price, scope, target customer, the non-goals, each risky assumption — become typed cards: structured, addressable by ID, citable from the PRD instead of buried in a doc. The narrative stays a narrative; the <em>decisions</em> get a spine.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Change the pricing decision and every card that rested on it — the external FAQ answer, the PRD section, the spec that assumed per-use billing — turns stale automatically, the way a build system invalidates everything downstream of a changed file. The PR/FAQ stops being a snapshot and starts tracking reality.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes the whole thing</h3>
            <p>Claude Code or Cursor, over MCP, reads the press release and both FAQs before it drafts the PRD, and writes new decisions back into the same structure. The working-backwards thinking you did up front becomes living context the agent honors on every turn — not a document a reader never reopens.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Working backwards is the right way to think. The mistake is freezing the thinking in a document the moment you finish it.
        </blockquote>
        <cite>Keep the method. Let the substrate keep the decisions current.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Write your PR/FAQ <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Draft the press release and FAQ the Amazon way, let the agent turn the decisions inside into cards, and keep every one of them — and the PRD that depends on them — current as you learn.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
